import { useEffect, useRef, useState, type ReactNode } from 'react';
import L from 'leaflet';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { useI18n } from '@/i18n/i18n-provider';
import { Alert, Button, Field } from '@/components/ui';

/**
 * Not `L.Icon.Default.mergeOptions(...)` — the well-known fix everyone
 * reaches for first, and the wrong one. `Icon.Default`'s own
 * `_getIconUrl` (see leaflet-src.js) unconditionally *prepends* an
 * auto-detected `imagePath` to whatever `iconUrl` is set to, even after
 * `mergeOptions` overrides it — pointing a bundler at a Vite dev URL
 * doubles that URL onto itself and the marker never loads. A plain
 * `L.icon(...)` instance, passed explicitly per `<Marker>`, uses the base
 * `Icon.prototype._getIconUrl` instead, which has no such prepending.
 * Its size/anchor values below are `Icon.Default`'s own hard-coded
 * defaults, copied so the marker looks identical to Leaflet's stock pin.
 */
const pinIcon = L.icon({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

/** No church location set yet — Santo Domingo, a reasonable starting view
 *  rather than the Atlantic off Null Island (0,0). */
const DEFAULT_CENTER: [number, number] = [18.4861, -69.9312];
const DEFAULT_ZOOM = 12;
const PIN_ZOOM = 16;

interface NominatimResult {
  readonly lat: string;
  readonly lon: string;
}

async function geocode(query: string): Promise<{ lat: number; lng: number } | null> {
  const url = new URL('https://nominatim.openstreetmap.org/search');
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('limit', '1');
  url.searchParams.set('q', query);

  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) return null;

  const results = (await response.json()) as readonly NominatimResult[];
  const first = results[0];
  if (!first) return null;

  return { lat: Number(first.lat), lng: Number(first.lon) };
}

function ClickToPlace({ onPlace }: { readonly onPlace: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(event) {
      onPlace(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

/** Recenters the map when the pin moves for a reason other than dragging it
 *  on this same map — a search result, or the parent loading saved data. */
function RecenterOnChange({ lat, lng }: { readonly lat: number; readonly lng: number }) {
  const map = useMap();
  const previous = useRef({ lat, lng });

  useEffect(() => {
    if (previous.current.lat !== lat || previous.current.lng !== lng) {
      map.setView([lat, lng], Math.max(map.getZoom(), PIN_ZOOM));
      previous.current = { lat, lng };
    }
  }, [lat, lng, map]);

  return null;
}

/**
 * A pin the caller can place three ways — search an address, click the map,
 * or drag the marker — all converging on the same `onChange(lat, lng)`.
 * OpenStreetMap + Nominatim: no API key, no account, nothing to configure.
 */
export function LocationMapPicker({
  latitude,
  longitude,
  onChange,
}: {
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly onChange: (latitude: number, longitude: number) => void;
}): ReactNode {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const position: [number, number] =
    latitude !== null && longitude !== null ? [latitude, longitude] : DEFAULT_CENTER;

  async function handleSearch(): Promise<void> {
    const trimmed = query.trim();
    if (!trimmed) return;

    setIsSearching(true);
    setSearchError(null);
    try {
      const found = await geocode(trimmed);
      if (found) {
        onChange(found.lat, found.lng);
      } else {
        setSearchError(t('administration.tenant.location.searchNoResults'));
      }
    } catch {
      setSearchError(t('administration.tenant.location.searchFailed'));
    } finally {
      setIsSearching(false);
    }
  }

  return (
    <div className="location-map-picker">
      {/* A `<div>`, not a `<form>` — this picker is used inside the
          church-profile form, and HTML forbids a nested <form>; a real one
          here would let Enter/click submit the *outer* form instead. */}
      <div className="location-map-search">
        <Field
          label={t('administration.tenant.location.searchLabel')}
          name="locationSearch"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void handleSearch();
            }
          }}
          placeholder={t('administration.tenant.location.searchPlaceholder')}
        />
        <Button
          type="button"
          variant="secondary"
          loading={isSearching}
          onClick={() => void handleSearch()}
        >
          {t('common.actions.search')}
        </Button>
      </div>

      {searchError && <Alert tone="danger">{searchError}</Alert>}

      <p className="field-hint">{t('administration.tenant.location.mapHint')}</p>

      <div className="location-map-container">
        <MapContainer
          center={position}
          zoom={DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker
            position={position}
            icon={pinIcon}
            draggable
            eventHandlers={{
              dragend: (event: L.LeafletEvent) => {
                const { lat, lng } = (event.target as L.Marker).getLatLng();
                onChange(lat, lng);
              },
            }}
          />
          <ClickToPlace onPlace={onChange} />
          <RecenterOnChange lat={position[0]} lng={position[1]} />
        </MapContainer>
      </div>
    </div>
  );
}
